import type {
  ChatMessageContent,
  ChatMessageKind,
  ChatMessageStatus,
} from './conversationalChat';

export type ChatSessionMode =
  'onboarding' | 'benefits-calculator' | 'pet-profile';

/**
 * Whether the agent is working on a turn, in a conversation the server runs,
 * or waiting on something sent, such as a file, being read.
 */
export type AgentStatus = 'thinking' | 'reading' | 'idle';

/** The session metadata the server keeps its agent's status in. */
export const AGENT_STATUS_METADATA_KEY = 'agentStatus';

/** The session metadata the server keeps what its agent has done in the turn it's working on, a line each. */
export const AGENT_ACTIVITY_METADATA_KEY = 'agentActivity';

/** A conversation the server runs in chat sessions of its mode. */
export interface ConversationConfig {
  mode: ChatSessionMode;
  title: string;
  description: string;
  /** For trying the chat engine out; never offered in production. */
  isSandbox: boolean;
}
export type ChatSessionStatus = 'active' | 'completed' | 'abandoned';

/** Why the care profile didn't accept an answer a message carried. */
export interface CareProfileError {
  questionId?: string;
  code:
    | 'unknown_question'
    | 'unsupported_field_type'
    | 'invalid_option'
    | 'too_many_options'
    | 'invalid_value';
}

/** What a session keeps besides its messages: what the server says of its agent, and whatever else the app keeps. */
export interface ChatSessionMetadata extends Record<string, unknown> {
  [AGENT_STATUS_METADATA_KEY]?: AgentStatus;
  [AGENT_ACTIVITY_METADATA_KEY]?: string[];
}

export interface ChatSessionResponse {
  id: string;
  careCircleId: string;
  primaryAccountId: string;
  mode: string;
  status: string;
  metadata?: ChatSessionMetadata;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessageResponse {
  id: string;
  sessionId: string;
  senderKind: 'system' | 'user';
  senderAccountId?: string;
  contentKind: ChatMessageKind;
  content: ChatMessageContent;
  documentId?: string;
  status: string;
  /** Set when the care profile didn't accept the answers the message carried. */
  careProfileErrors?: CareProfileError[];
  createdAt: string;
}

export interface ChatSessionWithMessagesResponse {
  session: ChatSessionResponse;
  messages: ChatMessageResponse[];
}

export interface CreateChatSessionRequest {
  mode: ChatSessionMode;
  metadata?: Record<string, unknown>;
}

export interface UpdateChatSessionRequest {
  metadata?: Record<string, unknown>;
  status?: ChatSessionStatus;
}

export interface AppendChatMessageRequest {
  /** Client-generated UUID; appending is idempotent on it. */
  id?: string;
  senderKind: 'system' | 'user';
  contentKind: ChatMessageKind;
  content: ChatMessageContent;
  documentId?: string;
  status: ChatMessageStatus;
}

export interface UpdateChatMessageRequest {
  documentId?: string;
  status?: ChatMessageStatus;
}
